import { useLocation } from "@tanstack/react-router";
import { logout } from "lib/auth";
import {
  aboutPath,
  accountSwitcherPath,
  adminPath,
  blogPath,
  careersPath,
  locationsPath,
  contactFormPath,
  forgotPasswordPath,
  homePath,
  loginPath,
  solutionsPath,
  studioPath,
  updatePasswordPath,
} from "lib/routeHelpers";
import { useUserContext } from "lib/state/UserContextProvider";
import type { ReactNode } from "react";
import React, { useState } from "react";
import Link from "ui/components/Link";
import cx from "ui/cx";
import IconHigharcLockupHorizontal from "ui/icons/IconHigharcLockupHorizontal";
import styles from "./Navbar.module.scss";

interface NavbarLinkProps extends React.HTMLProps<HTMLAnchorElement> {
  children: ReactNode;
  primary?: boolean;
  current?: boolean;
}

function NavbarLink({ children, primary = false, current = false, href, ...props }: NavbarLinkProps) {
  const classNames = cx({ styles, link: true, primary, current });

  return (
    <Link
      href={href}
      {...props}
      className={classNames}
      onClick={
        // This is a bit of a kludge because it works around the type safety of the event handler.
        props.onClick
          ? (e: React.MouseEvent<HTMLAnchorElement> | null) => props.onClick?.(e as React.MouseEvent<HTMLAnchorElement>)
          : undefined
      }
    >
      {children}
    </Link>
  );
}

interface NavbarMenuProps {
  ctaText: string;
  ctaHref: string;
}

interface NavbarItemsProps extends NavbarMenuProps {
  pathname: string;
}

function NavbarItems({ pathname, ctaText, ctaHref }: NavbarItemsProps) {
  const { user, isSuperAdmin } = useUserContext();
  const accounts = user?.builderAccounts ?? [];

  return (
    <div className={cx({ styles, items: true, user: !!user })}>
      <>
        {user ? (
          <>
            {isSuperAdmin && (
              <>
                <NavbarLink href={adminPath()}>Admin Dashboard</NavbarLink>
                <NavbarLink href={studioPath()}>Studio</NavbarLink>
              </>
            )}
            {accounts.length === 1 && !!accounts[0]?.builderId && (
              <NavbarLink href={locationsPath(accounts[0].builderId)}>Locations</NavbarLink>
            )}
            {accounts.length > 1 && <NavbarLink href={accountSwitcherPath()}>Accounts</NavbarLink>}
            <NavbarLink onClick={() => logout(user?.id)}>Sign out</NavbarLink>
          </>
        ) : (
          <>
            <NavbarLink href={ctaHref} primary>
              {ctaText}
            </NavbarLink>
            <NavbarLink href={solutionsPath}>Solutions</NavbarLink>
            <NavbarLink href={aboutPath} current={pathname.includes("about-us")}>
              About
            </NavbarLink>
            <NavbarLink href={careersPath} current={pathname.includes("careers")}>
              Careers
            </NavbarLink>
            <NavbarLink href={blogPath}>Blog</NavbarLink>
            <NavbarLink href={loginPath()}>Sign in</NavbarLink>
          </>
        )}
      </>
    </div>
  );
}

interface NavbarMenuButtonProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  homepage: boolean;
}

function NavbarMenuButton({ open, setOpen, homepage }: NavbarMenuButtonProps) {
  const classNames = cx({ styles, menu: true, open: open, closed: !open, homepage });
  return <div className={classNames} onClick={() => setOpen(!open)} />;
}

interface NavbarMobileMenuProps extends NavbarMenuProps {
  open: boolean;
  homepage: boolean;
}

function NavbarMobileMenu({ open, homepage, ctaText, ctaHref }: NavbarMobileMenuProps) {
  const { user, isSuperAdmin } = useUserContext();
  const classNames = cx({ styles, mobile: true, open: open, closed: !open, homepage });

  return (
    <div className={classNames}>
      <>
        {user ? (
          <>
            {isSuperAdmin && (
              <>
                <NavbarLink href={adminPath()}>Admin Dashboard</NavbarLink>
                <NavbarLink href={studioPath()}>Studio</NavbarLink>
              </>
            )}
            <NavbarLink onClick={() => logout(user?.id)}>Sign out</NavbarLink>
          </>
        ) : (
          <>
            <NavbarLink href={ctaHref} primary>
              {ctaText}
            </NavbarLink>
            <NavbarLink href={solutionsPath}>Solutions</NavbarLink>
            <NavbarLink href={aboutPath}>About</NavbarLink>
            <NavbarLink href={careersPath}>Careers</NavbarLink>
            <NavbarLink href={blogPath}>Blog</NavbarLink>
            <NavbarLink href={loginPath()}>Sign in</NavbarLink>
          </>
        )}
      </>
    </div>
  );
}

interface NavbarLogoProps {
  homepage: boolean;
}

function NavbarLogo({ homepage }: NavbarLogoProps) {
  return (
    <Link href={homePath}>
      <IconHigharcLockupHorizontal className={cx({ styles, logo: true, homepage })} />
    </Link>
  );
}

interface NavbarProps extends Partial<NavbarMenuProps> {
  homepage?: boolean;
}

export default function Navbar({ homepage = false, ctaText = "Get in Touch", ctaHref = contactFormPath }: NavbarProps) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const invitePath = "/invites/";
  const hiddenPaths = [contactFormPath, loginPath(), forgotPasswordPath(), updatePasswordPath(), invitePath];

  const hidden = new RegExp(hiddenPaths.join("|")).test(location.pathname);
  const classNames = cx({ styles, wrapper: !hidden, homepage: !hidden, hidden }, "grid-container");

  if (hidden) {
    return <div id="navbar" className={classNames} />;
  }

  return (
    <>
      <div id="navbar" className={classNames}>
        <NavbarLogo homepage={homepage} />
        <NavbarItems pathname={location.pathname} ctaText={ctaText} ctaHref={ctaHref} />
        <NavbarMenuButton open={open} setOpen={setOpen} homepage={homepage} />
        <NavbarMobileMenu open={open} homepage={homepage} ctaText={ctaText} ctaHref={ctaHref} />
      </div>
    </>
  );
}
